import { useState } from 'react';
import { FileText, Calendar, RefreshCw, Globe, Lock, Eye, Heart } from 'lucide-react';

interface Story {
  id: string;
  title: string;
  coverUrl?: string;
  status: 'DRAFT' | 'REVIEWING' | 'COMPLETED' | 'APPROVED' | 'REJECTED' | 'PUBLISHED';
  createdAt: string;
  pageCount: number;
  isPublic?: boolean;
  viewCount?: number;
  likeCount?: number;
}

interface StoryCardProps {
  story: Story;
  onClick?: () => void;
  onRegenerate?: () => void;
  onToggleVisibility?: (storyId: string, isPublic: boolean) => void;
  showVisibilityToggle?: boolean;
}

const statusMap: Record<string, { label: string; className: string }> = {
  DRAFT: { label: '草稿', className: 'bg-slate-100 text-slate-600' },
  REVIEWING: { label: '审核中', className: 'bg-amber-100 text-amber-700' },
  APPROVED: { label: '已通过', className: 'bg-emerald-100 text-emerald-700' },
  PUBLISHED: { label: '已发布', className: 'bg-emerald-100 text-emerald-700' },
  COMPLETED: { label: '已完成', className: 'bg-emerald-100 text-emerald-700' },
  REJECTED: { label: '已拒绝', className: 'bg-rose-100 text-rose-700' },
};

export default function StoryCard({ 
  story, 
  onClick, 
  onRegenerate, 
  onToggleVisibility,
  showVisibilityToggle = false 
}: StoryCardProps) {
  const status = statusMap[story.status] || statusMap.DRAFT;
  const [imgError, setImgError] = useState(false);
  
  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer"
    >
      {/* 封面图 */}
      <div className="relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100" onClick={onClick}>
        {story.coverUrl && !imgError ? (
          <img
            src={story.coverUrl}
            alt={story.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl">📚</span>
          </div>
        )}
        
        {/* 状态标签 */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3">
          <span className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium ${status.className}`}>
            {status.label}
          </span>
        </div>

        {/* 公开/私有标签 */}
        {showVisibilityToggle && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility?.(story.id, !story.isPublic);
              }}
              className={`flex items-center gap-0.5 sm:gap-1 px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium border transition-all ${
                story.isPublic
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200'
              }`}
              title={story.isPublic ? '点击设为私有' : '点击设为公开'}
            >
              {story.isPublic ? (
                <>
                  <Globe className="w-3 h-3" />
                  <span className="hidden sm:inline">公开</span>
                </>
              ) : (
                <>
                  <Lock className="w-3 h-3" />
                  <span className="hidden sm:inline">私有</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* 重新生成按钮 */}
        {onRegenerate && story.status === 'COMPLETED' && !showVisibilityToggle && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRegenerate();
            }}
            className="absolute top-3 right-3 p-2 bg-white/90 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            title="重新生成图片"
          >
            <RefreshCw className="w-4 h-4 text-slate-600" />
          </button>
        )}

        {/* 悬停遮罩 */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden sm:block">
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <span className="text-white text-sm font-medium">点击查看</span>
          </div>
        </div>
      </div>

      {/* 信息区 */}
      <div className="p-3 sm:p-4 md:p-5">
        <h3 className="font-bold text-slate-800 text-sm sm:text-base md:text-lg mb-1 md:mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
          {story.title}
        </h3>
        
        <div className="flex items-center justify-between text-[10px] sm:text-xs md:text-sm text-slate-500">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <span className="flex items-center gap-0.5 sm:gap-1">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
              {story.pageCount} 页
            </span>
            <span className="flex items-center gap-0.5 sm:gap-1">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
              {new Date(story.createdAt).toLocaleDateString('zh-CN', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="flex items-center gap-0.5" title="阅读量">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
              {story.viewCount || 0}
            </span>
            <span className="flex items-center gap-0.5" title="点赞量">
              <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
              {story.likeCount || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
